import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { takeUntil } from 'rxjs';
import { TranslatePipe } from '@newroom-connect/library/pipes';
import { PixelStreamService, LoggingService, TrackingEventService } from '@newroom-connect/library/services';
import { ICrossAppMessageData, SupportActionEvents } from '@newroom-connect/library/interfaces';
import { TrackingEventType } from '@newroom-connect/library/enums';

import { DestroyableComponent } from '../abstract/destroyable/destroyable.component';

@Component({
  selector: 'nrc-pixel-stream',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe
  ],
  templateUrl: './pixel-stream.component.html',
  styleUrls: ['./pixel-stream.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PixelStreamComponent extends DestroyableComponent implements OnInit, OnDestroy {
  private readonly pixelStreamService = inject(PixelStreamService);
  private readonly loggingService = inject(LoggingService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly trackingEventService = inject(TrackingEventService);

  @Input({ required: true }) public streamId!: string;
  @Input({ required: true }) public baseUrl!: string;
  @Input({ required: true }) public sdkUrl!: string;
  @Input() public isInteractive = true;

  @Output() public streamConnected = new EventEmitter<void>();
  @Output() public streamDisconnected = new EventEmitter<void>();
  @Output() public streamError = new EventEmitter<Error>();
  @Output() public streamMessage = new EventEmitter<ICrossAppMessageData>();
  @Output() public pixelStreamTrackingEvent = new EventEmitter<ICrossAppMessageData>();

  protected readonly showStream = signal<boolean>(false);
  protected streamUrl: SafeResourceUrl | null = null;

  /**
   *
   */
  public ngOnInit(): void {
    if (!this.streamId) {
      this.loggingService.error('PixelStreamComponent: streamId is required');
      return;
    }

    // Build stream URL
    this.streamUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.baseUrl}${this.streamId}`);

    // Load SDK if interactive mode
    if (this.isInteractive) {
      this.loadSDK();
    }
  }

  /**
   *
   */
  public override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.pixelStreamService.reset();
  }

  /**
   *
   */
  public async startStream(): Promise<void> {
    if (this.showStream()) {
      return;
    }

    this.pixelStreamService.updateConnectionStatus('connecting');

    try {
      await this.loadSDK();

      this.setupEventListeners();

      // Start initialization by displaying the stream frame
      this.showStream.set(true);
    } catch (err) {
      this.loggingService.error('Failed to start pixel stream', err);
    }
  }

  /**
   *
   */
  private async loadSDK(): Promise<void> {
    try {
      await this.pixelStreamService.loadSDK(this.sdkUrl);
    } catch (err) {
      this.loggingService.error('Failed to load Vagon SDK', err);

      throw err;
    }
  }

  /**
   *
   */
  private setupEventListeners(): void {
    // Initialize Vagon SDK event listeners
    this.pixelStreamService.initializeEventListeners();

    // Subscribe to connection events
    this.pixelStreamService.connected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loggingService.info('Pixel stream connected');

        this.streamConnected.emit();
      });

    this.pixelStreamService.disconnected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loggingService.info('Pixel stream disconnected');

        this.showStream.set(false);
        this.streamDisconnected.emit();
      });

    this.pixelStreamService.connectionError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        this.loggingService.error('Pixel stream connection error', error);

        this.streamError.emit(error);
      });

    // Subscribe to stream messages
    this.pixelStreamService.streamMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((streamMessage) => {
        this.loggingService.debug('Received message from stream', streamMessage);

        // Only create tracking event; skip background event processing.
        if (streamMessage.action.includes(SupportActionEvents.CREATE_TRACKING_EVENT)) {
          this.trackingEventService.createTrackingEvent({ eventType: TrackingEventType.CROSS_APP_TRACKING_EVENT, metadata: streamMessage.metadata });
          return;
        }

        this.streamMessage.emit(streamMessage);
      });
  }
}
